/**
 * SCR-033 — Email verification callback.
 * Public route. Reads ?token= query param, auto-calls API on mount.
 * States: verifying → success | error. Error shows resend form with 240s cooldown.
 */
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@client/lib/auth-client';

const searchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute('/verify-email')({
  validateSearch: zodValidator(searchSchema),
  component: VerifyEmailPage,
});

type VerifyState = 'verifying' | 'success' | 'error';
const COOLDOWN_SECS = 240;

const resendSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
});
type ResendForm = z.infer<typeof resendSchema>;

function VerifyEmailPage() {
  const { token } = useSearch({ from: '/verify-email' });

  const [state, setState] = useState<VerifyState>(token ? 'verifying' : 'error');
  const [resendAlert, setResendAlert] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const verifiedRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResendForm>({ resolver: zodResolver(resendSchema) });

  useEffect(() => {
    if (!token || verifiedRef.current) return;
    verifiedRef.current = true;

    authClient.verifyEmail({ query: { token } })
      .then(({ error }) => {
        if (error) setState('error');
        else setState('success');
      })
      .catch(() => setState('error'));
  }, [token]);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  async function onResend(values: ResendForm) {
    setResendAlert(null);
    try {
      await authClient.sendVerificationEmail({
        email: values.email.trim().toLowerCase(),
      });
    } catch {
      // Always show generic message (chống dò tài khoản)
    }
    setResendAlert(
      'Nếu email tồn tại và chưa xác thực, chúng tôi đã gửi lại liên kết xác thực.',
    );
    startCooldown();
  }

  function startCooldown() {
    setCooldown(COOLDOWN_SECS);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { if (timerRef.current) clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary shadow-md">
            <span className="text-lg font-bold text-primary-foreground">CLC</span>
          </div>
          <h2 className="text-xl font-semibold text-foreground">Xác thực email</h2>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 shadow-sm flex flex-col gap-4 items-center text-center">
          {/* Verifying */}
          {state === 'verifying' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <h3 className="text-lg font-semibold">Đang xác thực email…</h3>
              <p className="text-sm text-muted-foreground">Vui lòng chờ trong giây lát.</p>
            </>
          )}

          {/* Success */}
          {state === 'success' && (
            <>
              <CheckCircle2 className="h-12 w-12 text-primary" />
              <h3 className="text-lg font-semibold text-primary">Xác thực email thành công</h3>
              <p className="text-sm text-muted-foreground">
                Bạn có thể đăng nhập ngay bây giờ.
              </p>
              <Button asChild className="w-full h-11 mt-2">
                <a href="/login">Đến trang đăng nhập</a>
              </Button>
            </>
          )}

          {/* Error */}
          {state === 'error' && (
            <div className="w-full flex flex-col gap-4 items-center text-center">
              <AlertTriangle className="h-12 w-12 text-brand-orange-500" />
              <h3 className="text-lg font-semibold">
                {token
                  ? 'Liên kết xác thực không hợp lệ hoặc đã hết hạn'
                  : 'Chưa có liên kết xác thực'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {token
                  ? 'Liên kết đã hết hạn. Nhập email để nhận lại liên kết mới.'
                  : 'Nhập email để nhận liên kết xác thực tài khoản.'}
              </p>

              {resendAlert && (
                <Alert className="text-left">
                  <AlertDescription>{resendAlert}</AlertDescription>
                </Alert>
              )}

              <form
                onSubmit={handleSubmit(onResend)}
                noValidate
                className="w-full flex flex-col gap-3 text-left"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="resend-email" className="text-sm font-medium">
                    Email
                  </Label>
                  <Input
                    id="resend-email"
                    type="email"
                    autoComplete="email"
                    placeholder="name@clc.edu.vn"
                    className="h-11"
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive">{errors.email.message}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="h-11 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                  disabled={isSubmitting || cooldown > 0}
                >
                  {cooldown > 0
                    ? `Gửi lại sau ${cooldown}s`
                    : isSubmitting
                      ? 'Đang gửi…'
                      : 'Gửi lại email xác thực'}
                </Button>
              </form>
            </div>
          )}

          {/* Footer link — always shown */}
          {state !== 'verifying' && (
            <a
              href="/login"
              className="text-sm text-primary hover:text-primary/80 transition-colors mt-2"
            >
              Về trang đăng nhập
            </a>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          CLC Education LMS · v1.0
        </p>
      </div>
    </div>
  );
}
